import type { AppLocale, LocalePreference } from './types'

function localeFromTag(tag: string | undefined): AppLocale | null {
  const code = tag?.split('-')[0]?.toLowerCase()
  if (code === 'en') return 'en'
  if (code === 'es') return 'es'
  return null
}

function collectBrowserLanguageTags(): string[] {
  if (typeof navigator === 'undefined') return []

  const seen = new Set<string>()
  const tags: string[] = []

  const add = (raw: string | undefined) => {
    const normalized = raw?.trim()
    if (!normalized) return
    const key = normalized.toLowerCase()
    if (seen.has(key)) return
    seen.add(key)
    tags.push(normalized)
  }

  // Orden explícito de preferencias del navegador (Accept-Language).
  // Algunos navegadores (p. ej. Brave) pueden reportar `navigator.language`
  // distinto al primer elemento de `navigator.languages`.
  if (navigator.languages?.length) {
    for (const lang of navigator.languages) add(lang)
  }

  add(navigator.language)

  try {
    add(Intl.DateTimeFormat().resolvedOptions().locale)
  } catch {
    /* ignore */
  }

  return tags
}

export type BrowserLocaleDetection = {
  locale: AppLocale
  /** Etiqueta BCP-47 que determinó el idioma. */
  decidingTag: string | null
  /** Todas las etiquetas consideradas, en orden de prioridad. */
  tags: string[]
}

export function detectBrowserLocaleWithMeta(): BrowserLocaleDetection {
  const tags = collectBrowserLanguageTags()

  for (const tag of tags) {
    const match = localeFromTag(tag)
    if (match) {
      return { locale: match, decidingTag: tag, tags }
    }
  }

  return { locale: 'en', decidingTag: tags[0] ?? null, tags }
}

/** Idioma del navegador para modo automático. */
export function detectBrowserLocale(): AppLocale {
  return detectBrowserLocaleWithMeta().locale
}

export function getBrowserLanguageTagsForDebug(): string[] {
  return collectBrowserLanguageTags()
}

export function resolveAppLocale(preference: LocalePreference): AppLocale {
  if (preference === 'auto') return detectBrowserLocale()
  return preference
}

export function describeAutoLocaleDetection(): BrowserLocaleDetection {
  return detectBrowserLocaleWithMeta()
}
